import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import type { TextareaHTMLAttributes } from 'react';

/** Default number of lines an `autoGrow` field grows to before it scrolls. */
const DEFAULT_MAX_ROWS = 5;

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label displayed above the textarea */
  label?: string;
  /** Helper text displayed below the textarea */
  helperText?: string;
  /** Error message — replaces helperText and triggers error styling */
  error?: string;
  /** Whether the textarea stretches to full container width */
  fullWidth?: boolean;
  /**
   * Start at a single line (matching the `Input` primitive's height) and grow
   * with the content, instead of the default fixed 100px box. Requires a
   * controlled `value` — the height is re-measured whenever it changes.
   */
  autoGrow?: boolean;
  /** With `autoGrow`, the number of lines to grow to before scrolling. */
  maxRows?: number;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      error,
      fullWidth,
      autoGrow,
      maxRows = DEFAULT_MAX_ROWS,
      className,
      ...rest
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    const setRef = useCallback(
      (el: HTMLTextAreaElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') {
          ref(el);
        } else if (ref) {
          ref.current = el;
        }
      },
      [ref],
    );

    // Sync the height to the content before paint, so the field never flashes
    // at the previous size. The CSS min/max-height clamp the measurement, so a
    // bogus mid-layout `scrollHeight` can neither collapse nor overgrow it.
    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el || !autoGrow) return;
      el.style.height = 'auto';
      // +2 covers the 1px top/bottom borders (the field is border-box).
      el.style.height = `${el.scrollHeight + 2}px`;
    }, [autoGrow, rest.value]);

    return (
      <TextareaWrapper className={className} $fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <StyledTextarea
          ref={setRef}
          $hasError={!!error}
          $autoGrow={autoGrow}
          $maxRows={maxRows}
          rows={autoGrow ? 1 : undefined}
          {...rest}
        />
        {(error || helperText) && (
          <HelperText $isError={!!error}>{error || helperText}</HelperText>
        )}
      </TextareaWrapper>
    );
  },
);

Textarea.displayName = 'Textarea';

export default Textarea;

const TextareaWrapper = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
`;

// Matches the `Input` primitive's label: a step below the value it names.
const Label = styled.label`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);
`;

// One line of text plus the field's vertical padding and 1px borders — the
// resting height of the `autoGrow` variant, matching the `Input` primitive.
const autoGrowLines = (lines: number) =>
  `calc(${lines} * var(--leading-text-sm) + 2 * var(--spacing-sm) + 2px)`;

const StyledTextarea = styled.textarea<{
  $hasError?: boolean;
  $autoGrow?: boolean;
  $maxRows?: number;
}>`
  width: 100%;
  /* Required, not cosmetic: width 100% alongside horizontal padding and a border
     overflows the container without it, and this app has no global box-sizing
     reset. common/Input and common/Select share the omission — left alone here
     only because they have other consumers. */
  box-sizing: border-box;
  min-height: ${props => (props.$autoGrow ? autoGrowLines(1) : '100px')};
  ${props =>
    props.$autoGrow &&
    `
      max-height: ${autoGrowLines(props.$maxRows ?? DEFAULT_MAX_ROWS)};
      overflow-y: auto;
    `}
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--input-background);
  border: 1px solid
    ${props => (props.$hasError ? 'var(--destructive)' : 'var(--border)')};
  border-radius: var(--radius-input);
  outline: none;
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
  /* autoGrow drops the handle: the JS height sync would fight the user's drag
     on every keystroke anyway. */
  resize: ${props => (props.$autoGrow ? 'none' : 'vertical')};
  transition: border-color var(--transition-fast);

  /* Soft escalation below the focus state (docs/design-system.md → Form Fields). */
  &:hover:not(:focus):not(:disabled) {
    border-color: ${props =>
      props.$hasError ? 'var(--destructive)' : 'var(--text-muted)'};
  }

  &:focus {
    border-color: ${props =>
      props.$hasError ? 'var(--destructive)' : 'var(--focus-border)'};
    box-shadow: 0 0 0 1px
      ${props => (props.$hasError ? 'var(--destructive)' : 'var(--focus-ring)')};
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    resize: none;
  }
`;

const HelperText = styled.span<{ $isError?: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  color: ${props =>
    props.$isError ? 'var(--destructive)' : 'var(--text-muted)'};
`;
